import { FileText, Sparkles } from 'lucide-react-native';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { Alert, ScrollView, StyleSheet } from 'react-native';
import { Box, HStack, Sheet, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import {
  useCreateSkill,
  useDeleteSkill,
  useOrgAgentSkills,
  useToggleSkill,
  useUpdateCustomInstructions,
  useUpdateSkill,
} from '../hooks';
import { useAgentDisplayName } from '../hooks/useAgentDisplayName';
import type { AgentConfig, AgentType, CustomInstructionsSource, OrgAgentSkill } from '../types';
import { CreateSkillForm } from './CreateSkillForm';
import { CustomInstructionsEditor } from './CustomInstructionsEditor';
import { SkillToggleCard } from './SkillToggleCard';

interface AgentCustomizationSheetProps {
  agentType: AgentType | null;
  organizationId: string;
  config?: AgentConfig;
  isOpen: boolean;
  onClose: () => void;
}

type Tab = 'skills' | 'instructions';

export function AgentCustomizationSheet({
  agentType,
  organizationId,
  config,
  isOpen,
  onClose,
}: Readonly<AgentCustomizationSheetProps>) {
  const { t } = useTranslation();
  const agentName = useAgentDisplayName();
  const [activeTab, setActiveTab] = useState<Tab>('skills');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSkill, setEditingSkill] = useState<OrgAgentSkill | null>(null);

  const { data: orgSkills } = useOrgAgentSkills(organizationId, agentType || '');
  const toggleMutation = useToggleSkill();
  const instructionsMutation = useUpdateCustomInstructions();
  const createMutation = useCreateSkill();
  const updateMutation = useUpdateSkill();
  const deleteMutation = useDeleteSkill();

  const customInstructions = useMemo(() => {
    if (!config || !agentType) return '';
    return config.customPrompts?.[agentType] || '';
  }, [config, agentType]);

  const handleToggleSkill = useCallback(
    (skillId: string, enabled: boolean) => {
      toggleMutation.mutate({ organizationId, skillId, enabled });
    },
    [organizationId, toggleMutation],
  );

  const handleSaveInstructions = useCallback(
    (instructions: string, source: CustomInstructionsSource) => {
      if (!agentType) return;
      instructionsMutation.mutate({
        organizationId,
        agentType,
        instructions,
        source,
      });
    },
    [organizationId, agentType, instructionsMutation],
  );

  const handleCreateSkill = useCallback(
    (values: { name: string; description: string; promptSnippet: string; category: string }) => {
      if (!agentType) return;
      createMutation.mutate(
        { organizationId, agentType, ...values },
        {
          onSuccess: () => {
            setShowCreateForm(false);
          },
        },
      );
    },
    [organizationId, agentType, createMutation],
  );

  const handleUpdateSkill = useCallback(
    (values: { name: string; description: string; promptSnippet: string; category: string }) => {
      if (!editingSkill) return;
      updateMutation.mutate(
        { organizationId, skillId: editingSkill.id, ...values },
        {
          onSuccess: () => {
            setEditingSkill(null);
          },
        },
      );
    },
    [organizationId, editingSkill, updateMutation],
  );

  const handleEditSkill = useCallback((skill: OrgAgentSkill) => {
    setEditingSkill(skill);
    setShowCreateForm(false);
  }, []);

  const handleDeleteSkill = useCallback(
    (skillId: string) => {
      Alert.alert(
        t('agents.customization.deleteSkillTitle'),
        t('agents.customization.deleteSkillMessage'),
        [
          { text: t('common.actions.cancel'), style: 'cancel' },
          {
            text: t('common.actions.delete'),
            style: 'destructive',
            onPress: () => {
              deleteMutation.mutate({ organizationId, skillId });
            },
          },
        ],
      );
    },
    [organizationId, deleteMutation, t],
  );

  const { customSkills, predefinedGroups } = useMemo(() => {
    if (!orgSkills) return { customSkills: [], predefinedGroups: {} };
    const custom: typeof orgSkills = [];
    const groups: Record<string, typeof orgSkills> = {};
    for (const skill of orgSkills) {
      if (skill.isCustom) {
        custom.push(skill);
      } else {
        if (!groups[skill.category]) {
          groups[skill.category] = [];
        }
        groups[skill.category].push(skill);
      }
    }
    return { customSkills: custom, predefinedGroups: groups };
  }, [orgSkills]);

  if (!agentType) return null;

  const label = agentName(agentType);

  return (
    <Sheet
      visible={isOpen}
      onClose={onClose}
      title={t('agents.customization.configure', { name: label })}
      position="right"
      size="md"
    >
      <VStack spacing="md" style={styles.container}>
        <HStack spacing="xs">
          <TabButton
            label={t('agents.customization.skillsTab')}
            icon={
              <Sparkles
                size={14}
                color={activeTab === 'skills' ? colors.primary[400] : colors.text.tertiary}
              />
            }
            isActive={activeTab === 'skills'}
            onPress={() => setActiveTab('skills')}
          />
          <TabButton
            label={t('agents.customization.instructionsTab')}
            icon={
              <FileText
                size={14}
                color={activeTab === 'instructions' ? colors.primary[400] : colors.text.tertiary}
              />
            }
            isActive={activeTab === 'instructions'}
            onPress={() => setActiveTab('instructions')}
          />
        </HStack>

        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {activeTab === 'skills' && (
            <VStack spacing="md">
              {showCreateForm && (
                <CreateSkillForm
                  mode="create"
                  onSubmit={handleCreateSkill}
                  onCancel={() => setShowCreateForm(false)}
                  isSubmitting={createMutation.isPending}
                />
              )}
              {!showCreateForm && editingSkill && (
                <CreateSkillForm
                  mode="edit"
                  initialValues={{
                    name: editingSkill.name,
                    description: editingSkill.description,
                    promptSnippet: editingSkill.promptSnippet,
                    category: editingSkill.category,
                  }}
                  onSubmit={handleUpdateSkill}
                  onCancel={() => setEditingSkill(null)}
                  isSubmitting={updateMutation.isPending}
                />
              )}
              {!showCreateForm && !editingSkill && (
                <>
                  <Text variant="caption" color={colors.text.tertiary}>
                    {t('agents.customization.skillsDescription')}
                  </Text>

                  <Button
                    variant="ghost"
                    size="sm"
                    onPress={() => {
                      setShowCreateForm(true);
                      setEditingSkill(null);
                    }}
                  >
                    {t('agents.customization.createCustomSkill')}
                  </Button>

                  {customSkills.length > 0 && (
                    <VStack spacing="xs">
                      <Text
                        variant="caption"
                        color={colors.text.secondary}
                        style={styles.categoryLabel}
                      >
                        {t('agents.customization.yourCustomSkills')}
                      </Text>
                      {customSkills.map((skill) => (
                        <SkillToggleCard
                          key={skill.id}
                          skill={skill}
                          onToggle={handleToggleSkill}
                          isToggling={toggleMutation.isPending}
                          onEdit={handleEditSkill}
                          onDelete={handleDeleteSkill}
                        />
                      ))}
                    </VStack>
                  )}

                  {Object.entries(predefinedGroups).map(([category, skills]) => (
                    <VStack key={category} spacing="xs">
                      <Text
                        variant="caption"
                        color={colors.text.secondary}
                        style={styles.categoryLabel}
                      >
                        {category.replaceAll('_', ' ').toUpperCase()}
                      </Text>
                      {skills.map((skill) => (
                        <SkillToggleCard
                          key={skill.id}
                          skill={skill}
                          onToggle={handleToggleSkill}
                          isToggling={toggleMutation.isPending}
                        />
                      ))}
                    </VStack>
                  ))}

                  {(!orgSkills || orgSkills.length === 0) && (
                    <Box padding="lg" alignItems="center">
                      <Text variant="caption" color={colors.text.tertiary}>
                        {t('agents.customization.noSkills')}
                      </Text>
                    </Box>
                  )}
                </>
              )}
            </VStack>
          )}

          {activeTab === 'instructions' && (
            <CustomInstructionsEditor
              organizationId={organizationId}
              agentType={agentType}
              initialValue={customInstructions}
              onSave={handleSaveInstructions}
              isSaving={instructionsMutation.isPending}
            />
          )}
        </ScrollView>
      </VStack>
    </Sheet>
  );
}

function TabButton({
  label,
  icon,
  isActive,
  onPress,
}: Readonly<{
  label: string;
  icon: ReactNode;
  isActive: boolean;
  onPress: () => void;
}>) {
  return (
    <Box
      pressable
      onPress={onPress}
      borderRadius="md"
      padding="sm"
      backgroundColor={isActive ? colors.primary.muted : 'transparent'}
      style={[styles.tab, isActive && styles.tabActive]}
    >
      <HStack spacing="xs" align="center" justify="center">
        {icon}
        <Text
          variant="caption"
          color={isActive ? colors.primary[400] : colors.text.tertiary}
          style={isActive ? styles.tabTextActive : undefined}
        >
          {label}
        </Text>
      </HStack>
    </Box>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  tab: {
    flex: 1,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'transparent',
  },
  tabActive: {
    borderColor: colors.primary.subtle,
  },
  tabTextActive: {
    fontWeight: '600',
  },
  categoryLabel: {
    fontWeight: '700',
    fontSize: 10,
    letterSpacing: 1,
  },
});
